import { faHashtag } from "@fortawesome/duotone-light-svg-icons"
import {
  FlexBox,
  Typography,
  TextInput,
  Button,
  SPACING,
  JoonUIColor,
  ChevronRightIcon,
  Modal,
} from "@joonapp/web-shared"
import { useEffect } from "react"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { Toasts } from "../../components/toaster/Toaster"
import { createJoonAPIClient } from "../../util/joon-api"
import { useLinkTherapistModalStore } from "../care/therapists/useLinkTherapistModalStore"

interface PaywallReferralModalStore {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  code: string
  setCode: (code: string) => void
  isApplyingCode: boolean
  setIsApplyingCode: (isApplyingCode: boolean) => void
  error: string | null
  setError: (error: string | null) => void
  hasValidCode: boolean
  setHasValidCode: (hasAppliedCode: boolean) => void
  modalScreen: "referralChoice" | "friendReferralCode"
  setModalScreen: (modalScreen: "referralChoice" | "friendReferralCode") => void
}

export const applyReferralCode = (code: string) => {
  const API = createJoonAPIClient()
  return API.post("api/referral-codes/apply/", { code })
}

export const usePaywallReferralModalStore = create<PaywallReferralModalStore>(
  (set) => ({
    isOpen: false,
    onOpen: () => set({ isOpen: true, modalScreen: "referralChoice" }),
    onClose: () => set({ isOpen: false, error: null, code: "" }),
    code: "",
    setCode: (code) => set({ code }),
    isApplyingCode: false,
    setIsApplyingCode: (isApplyingCode) => set({ isApplyingCode }),
    error: null,
    setError: (error) => set({ error }),
    hasValidCode: false,
    setHasValidCode: (hasValidCode) => set({ hasValidCode }),
    modalScreen: "referralChoice",
    setModalScreen: (modalScreen) => set({ modalScreen }),
  })
)

const PaywallReferralModal = () => {
  const { isOpen, onClose, modalScreen, setModalScreen } =
    usePaywallReferralModalStore()
  const { onOpen: openLinkTherapistModal } = useLinkTherapistModalStore()
  const { t } = useTranslation()

  return (
    <Modal isOpen={isOpen} onClose={onClose} displayCloseIcon animate>
      {modalScreen === "referralChoice" ? (
        <FlexBox
          direction="column"
          justify="center"
          align="center"
          style={{ width: "min(350px, 95vw)", padding: SPACING.space6 }}
          gap={SPACING.space6}
        >
          <Typography variant="h3">{t("PAYWALL_REFERRAL_WHO")}</Typography>
          <FlexBox direction="column" gap={SPACING.space4} align="center">
            <button
              style={buttonStyle}
              onClick={() => {
                onClose()
                openLinkTherapistModal(true)
              }}
            >
              <Typography variant="bodyBold">
                {t("PAYWALL_REFERRAL_THERAPIST")}
              </Typography>
              <ChevronRightIcon color={JoonUIColor.text.primary} />
            </button>
            <button
              style={buttonStyle}
              onClick={() => setModalScreen("friendReferralCode")}
            >
              <Typography variant="bodyBold">
                {t("PAYWALL_REFERRAL_ANOTHER_PARENT")}
              </Typography>
              <ChevronRightIcon color={JoonUIColor.text.primary} />
            </button>
          </FlexBox>
        </FlexBox>
      ) : (
        <FriendReferralCodeScreen />
      )}
    </Modal>
  )
}

const buttonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  borderRadius: SPACING.space2,
  border: `1px solid ${JoonUIColor.border.default}`,
  boxShadow: "2px 2px 2px 0px #F0F3F8",
  padding: `${SPACING.space3} ${SPACING.space4}`,
}

const FriendReferralCodeScreen = () => {
  const {
    onClose,
    code,
    setCode,
    isApplyingCode,
    setIsApplyingCode,
    error,
    setError,
    hasValidCode,
    setHasValidCode,
  } = usePaywallReferralModalStore()
  const { t } = useTranslation()

  const validateCode = async () => {
    setIsApplyingCode(true)
    try {
      await applyReferralCode(code.toUpperCase())
      setError(null)
      setHasValidCode(true)
    } catch (error) {
      setError(t("PAYWALL_REFERRAL_INVALID_CODE"))
      setHasValidCode(false)
    }
    setIsApplyingCode(false)
  }

  useEffect(() => {
    if (code.length === 6) validateCode()
    else {
      setHasValidCode(false)
      setError("")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  return (
    <FlexBox
      direction="column"
      justify="center"
      align="center"
      style={{ width: "min(350px, 95vw)", padding: SPACING.space6 }}
      gap={SPACING.space6}
    >
      <Typography variant="h3">{t("PAYWALL_REFERRAL_ENTER_CODE")}</Typography>
      <TextInput
        value={code}
        icon={faHashtag}
        name="referralCode"
        maxLength={6}
        onChange={(e: any) => setCode(e.target.value)}
        placeholder={"ABC123"}
        fullWidth
      />
      <FlexBox direction="column" gap={SPACING.space1} align="center">
        {error && (
          <Typography
            variant="bodySmallBold"
            color={JoonUIColor.semantic.alert}
          >
            {error}
          </Typography>
        )}
        <Button
          text={t("PAYWALL_REFERRAL_APPLY")}
          onClick={() => {
            onClose()
            toast(Toasts.parentPaywallDiscount)
          }}
          fullWidth
          isLoading={isApplyingCode}
          disabled={!hasValidCode || isApplyingCode}
        />
      </FlexBox>
    </FlexBox>
  )
}

export default PaywallReferralModal
